/** App RBAC — sidebar module codes match app_rbac_modules.code */

export const KNOWN_APP_MODULE_CODES = [
  'jobs',
  'candidates',
  'assessments',
  'messenger',
  'onboarding',
  'insights',
  'settings',
];

export const APP_MENU_MODULE_BY_NAME = {
  Jobs: 'jobs',
  Candidates: 'candidates',
  Assessments: 'assessments',
  Messenger: 'messenger',
  Onboarding: 'onboarding',
  'Insights ': 'insights',
  Insights: 'insights',
  Settings: 'settings',
};

/** Canonical permission codes (must match app_rbac_permissions.code). */
export const P = {
  JOBS_JOB_READ: 'jobs.job.read',
  JOBS_JOB_CREATE: 'jobs.job.create',
  JOBS_JOB_EDIT: 'jobs.job.edit',
  JOBS_JOB_DELETE: 'jobs.job.delete',
  JOBS_ASSIGNMENT_READ: 'jobs.assignment.read',
  JOBS_ASSIGNMENT_CREATE: 'jobs.assignment.create',
  JOBS_ASSIGNMENT_EDIT: 'jobs.assignment.edit',
  JOBS_ASSIGNMENT_DELETE: 'jobs.assignment.delete',
  JOBS_CANDIDATE_READ: 'jobs.candidate.read',
  JOBS_CANDIDATE_CREATE: 'jobs.candidate.create',
  JOBS_CANDIDATE_EDIT: 'jobs.candidate.edit',
  JOBS_CANDIDATE_DELETE: 'jobs.candidate.delete',
  JOBS_SETTINGS_READ: 'jobs.settings.read',
  JOBS_SETTINGS_EDIT: 'jobs.settings.edit',
  JOBS_AUTOMATION_READ: 'jobs.automation.read',
  JOBS_AUTOMATION_EDIT: 'jobs.automation.edit',
  JOBS_JOB_BOARD_READ: 'jobs.job_board.read',
  JOBS_JOB_BOARD_PUBLISH: 'jobs.job_board.publish',
  CANDIDATES_CANDIDATE_READ: 'candidates.candidate.read',
  CANDIDATES_CANDIDATE_CREATE: 'candidates.candidate.create',
  ASSESSMENTS_ASSESSMENT_READ: 'assessments.assessment.read',
  ASSESSMENTS_ASSESSMENT_CREATE: 'assessments.assessment.create',
  ASSESSMENTS_ASSESSMENT_EDIT: 'assessments.assessment.edit',
  MESSENGER_THREAD_READ: 'messenger.thread.read',
  MESSENGER_THREAD_CREATE: 'messenger.thread.create',
  ONBOARDING_FLOW_READ: 'onboarding.flow.read',
  ONBOARDING_FLOW_CREATE: 'onboarding.flow.create',
  ONBOARDING_FLOW_EDIT: 'onboarding.flow.edit',
  ONBOARDING_FLOW_DELETE: 'onboarding.flow.delete',
  INSIGHTS_REPORT_READ: 'insights.report.read',
  SETTINGS_PROFILE_READ: 'settings.profile.read',
  SETTINGS_PROFILE_EDIT: 'settings.profile.edit',
  SETTINGS_COMPANY_READ: 'settings.company.read',
  SETTINGS_COMPANY_CREATE: 'settings.company.create',
  SETTINGS_COMPANY_EDIT: 'settings.company.edit',
  SETTINGS_CLIENT_COMPANY_READ: 'settings.client_company.read',
  SETTINGS_CLIENT_COMPANY_CREATE: 'settings.client_company.create',
  SETTINGS_CLIENT_COMPANY_EDIT: 'settings.client_company.edit',
  SETTINGS_MEMBERS_READ: 'settings.members.read',
  SETTINGS_MEMBERS_CREATE: 'settings.members.create',
  SETTINGS_MEMBERS_EDIT: 'settings.members.edit',
  SETTINGS_ROLES_READ: 'settings.roles.read',
  SETTINGS_ROLES_CREATE: 'settings.roles.create',
  SETTINGS_ROLES_EDIT: 'settings.roles.edit',
  SETTINGS_ROLES_DELETE: 'settings.roles.delete',
  SETTINGS_BILLING_READ: 'settings.billing.read',
  SETTINGS_BILLING_EDIT: 'settings.billing.edit',
  SETTINGS_INTEGRATION_READ: 'settings.integration.read',
  SETTINGS_INTEGRATION_EDIT: 'settings.integration.edit',
};

/**
 * Filter layout menuList by module_codes from /auth/me company_user.
 * Super-admin (roleId === "1") bypasses this filter at the call site.
 */
export function filterMenuByModuleCodes(menuList = [], moduleCodes = null) {
  if (!Array.isArray(menuList)) return [];
  // null/undefined = no RBAC context yet → show full menu (legacy)
  if (moduleCodes == null) return menuList;
  const allowed = new Set((moduleCodes || []).map(String));
  return menuList.filter((item) => {
    const moduleCode = APP_MENU_MODULE_BY_NAME[item.name];
    if (!moduleCode) return true;
    return allowed.has(moduleCode);
  });
}

export function getPrimaryCompanyUser(meDetails) {
  const companies = meDetails?.companies || meDetails?.data?.companies || [];
  if (!companies.length) return null;
  return companies[0]?.company_user || null;
}

/** Only gate the menu when /auth/me says this membership uses new app RBAC. */
export function shouldApplyAppRbacMenu(companyUser) {
  if (!companyUser) return false;
  if (companyUser.rbac_source === 'app') return true;
  if (companyUser.rbac_source === 'legacy') return false;
  // Fallback: known module codes from new catalog
  const codes = companyUser.module_codes || [];
  return codes.some((c) => KNOWN_APP_MODULE_CODES.includes(String(c)));
}

/**
 * Modules the member may open from the sidebar: those with at least one `<module>.*.read`
 * permission. Create/edit alone (e.g. inviting candidates to a job) doesn't grant the page.
 */
export function getReadableModuleCodes(companyUser) {
  const modules = new Set();
  (companyUser?.permission_codes || []).forEach((code) => {
    const [moduleCode, , action] = String(code).split('.');
    if (action === 'read') modules.add(moduleCode);
  });
  return [...modules];
}

/**
 * Sidebar rules for the signed-in member. Members with app RBAC roles are filtered by
 * the modules they can read; everyone else falls back to the account role name:
 * HIRING_MANAGER loses Settings, RECRUITER only gets Jobs and Messenger.
 * Backend may send "Manager", "Hiring Manager" or "HIRING_MANAGER", so spaces become underscores.
 */
export function getMemberMenuRules({ companyUser = null, isSuperAdmin = false, roleName = '' } = {}) {
  const useAppRbacMenu = !isSuperAdmin && shouldApplyAppRbacMenu(companyUser);
  const accountRole = String(roleName || '').trim().toUpperCase().replace(/\s+/g, '_');
  return {
    moduleCodes: useAppRbacMenu ? getReadableModuleCodes(companyUser) : null,
    permissionCodes: useAppRbacMenu ? companyUser?.permission_codes || [] : null,
    isRecruiter: !useAppRbacMenu && accountRole === 'RECRUITER',
    isHiringManager: !useAppRbacMenu && (accountRole === 'HIRING_MANAGER' || accountRole === 'MANAGER'),
  };
}

/** Read permission each Settings page needs; a path prefix also covers its sub-pages. */
export const SETTINGS_PAGE_READ_PERMISSION = {
  '/profile': P.SETTINGS_PROFILE_READ,
  '/client-companies': P.SETTINGS_CLIENT_COMPANY_READ,
  '/teams': P.SETTINGS_COMPANY_READ,
  '/team-members': P.SETTINGS_MEMBERS_READ,
  '/company-roles': P.SETTINGS_ROLES_READ,
  '/keys': P.SETTINGS_INTEGRATION_READ,
  '/plan': P.SETTINGS_BILLING_READ,
};

function settingsPageReadPermission(path = '') {
  const match = Object.keys(SETTINGS_PAGE_READ_PERMISSION).find(
    (prefix) => path === prefix || String(path).startsWith(`${prefix}/`)
  );
  return match ? SETTINGS_PAGE_READ_PERMISSION[match] : null;
}

/** permissionCodes null = no app RBAC (legacy / super admin), so every page stays open. */
export function canOpenSettingsPage(permissionCodes, path) {
  const required = settingsPageReadPermission(path);
  if (!required || permissionCodes == null) return true;
  return permissionCodes.includes(required);
}

export function filterMenuForMember(
  menuList = [],
  { moduleCodes = null, permissionCodes = null, isRecruiter = false, isHiringManager = false } = {}
) {
  return filterMenuByModuleCodes(menuList, moduleCodes)
    .filter((item) => {
      if (isRecruiter) return ['Jobs', 'Messenger'].includes(item.name);
      if (isHiringManager) return item.name !== 'Settings';
      return true;
    })
    .map((item) => {
      if (!item.subMenu || permissionCodes == null) return item;
      return { ...item, subMenu: item.subMenu.filter((sub) => canOpenSettingsPage(permissionCodes, sub.path)) };
    })
    .filter((item) => !item.subMenu || item.subMenu.length > 0);
}

/**
 * Check a single app permission code.
 * Returns true for legacy users (no app RBAC) so existing tenants keep working.
 */
export function hasAppPermission(meDetails, permissionCode) {
  if (!permissionCode) return true;
  const cu = getPrimaryCompanyUser(meDetails);
  if (!cu) return true; // no company context yet / legacy
  if (!shouldApplyAppRbacMenu(cu)) return true;
  const codes = cu.permission_codes || [];
  return codes.includes(permissionCode);
}

/** True if the user has any of the given permission codes. */
export function hasAnyAppPermission(meDetails, permissionCodes = []) {
  if (!permissionCodes.length) return true;
  return permissionCodes.some((code) => hasAppPermission(meDetails, code));
}

/** True if the user has all of the given permission codes. */
export function hasAllAppPermissions(meDetails, permissionCodes = []) {
  if (!permissionCodes.length) return true;
  return permissionCodes.every((code) => hasAppPermission(meDetails, code));
}

// Convenience wrappers (kept for existing call sites)
export function canCreateJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_CREATE);
}

export function canEditJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_EDIT);
}

export function canDeleteJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_DELETE);
}

/**
 * Assign stages, change hiring status and reassign candidates. Only custom roles are
 * restricted (by Job Assignment); the backend works it out as `can_assign_candidates`
 * because the account role, which every member has, must not count for custom roles.
 */
export function canAssignCandidates(meDetails) {
  const cu = getPrimaryCompanyUser(meDetails);
  if (!cu || !shouldApplyAppRbacMenu(cu)) return true;
  return cu.can_assign_candidates !== false;
}

/** Invite candidates into a job (job header + /invite page). */
export function canInviteCandidatesToJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_CANDIDATE_CREATE);
}

/**
 * Job Settings tab visibility (read or edit). Soft-fallback to jobs.job.edit.
 */
export function canViewJobSettings(meDetails) {
  return hasAnyAppPermission(meDetails, [
    P.JOBS_SETTINGS_READ,
    P.JOBS_SETTINGS_EDIT,
    P.JOBS_JOB_EDIT,
  ]);
}

/**
 * Job Settings write access. Soft-fallback to jobs.job.edit.
 */
export function canManageJobSettings(meDetails) {
  return hasAnyAppPermission(meDetails, [P.JOBS_SETTINGS_EDIT, P.JOBS_JOB_EDIT]);
}

/**
 * Automation tab visibility (read or edit). Soft-fallback to jobs.job.edit.
 */
export function canViewJobAutomation(meDetails) {
  return hasAnyAppPermission(meDetails, [
    P.JOBS_AUTOMATION_READ,
    P.JOBS_AUTOMATION_EDIT,
    P.JOBS_JOB_EDIT,
  ]);
}

/**
 * Automation write access. Soft-fallback to jobs.job.edit.
 */
export function canManageJobAutomation(meDetails) {
  return hasAnyAppPermission(meDetails, [P.JOBS_AUTOMATION_EDIT, P.JOBS_JOB_EDIT]);
}

/**
 * Account (login) roles from the assessment API, assignable via user/add-user.
 * Separate from app RBAC roles above. The API rejects a non-default role unless
 * the caller is SuperAdmin (1) or Admin (2).
 */
export const DEFAULT_ACCOUNT_ROLE_ID = 3;

export const ACCOUNT_ROLE_OPTIONS = [
  { id: 3, label: 'Recruiter' },
  { id: 7, label: 'Manager' },
  { id: 4, label: 'User' },
];

export function canAssignAccountRole(profileDetails) {
  return ['1', '2'].includes(String(profileDetails?.roleId ?? ''));
}
